// Équipements publics réservables (M19, lot L4-A, H5-06, D-145) : un équipement est un bien du
// patrimoine marqué réservable (salle, terrain de sport), avec un code généré (D-038), une capacité,
// des créneaux d'ouverture par jour de la semaine et, s'il est payant, une rubrique du catalogue des
// recettes. Réservations numérotées RES : demandée (portail ou guichet), acceptée à payer (créance
// émise par le module des recettes), confirmée (directement si gratuite, après paiement sinon),
// refusée avec motif, annulée, terminée. Deux réservations acceptées ou confirmées d'un même
// équipement ne se chevauchent jamais : contrainte d'exclusion en base, même en requêtes simultanées (R11).
import { z } from 'zod';
import { schemaHeure, schemaJourCivil } from './proprete';

const uuid = z.string().uuid('Identifiant invalide');

export const ETATS_RESERVATION = {
  demandee: 'Demandée',
  a_payer: 'Acceptée, en attente du paiement',
  confirmee: 'Confirmée',
  refusee: 'Refusée',
  annulee: 'Annulée',
  terminee: 'Terminée',
} as const;
export type EtatReservation = keyof typeof ETATS_RESERVATION;
export const CODES_ETATS_RESERVATION = Object.keys(ETATS_RESERVATION) as [EtatReservation, ...EtatReservation[]];
/** États qui occupent le créneau (contrainte d'exclusion). */
export const ETATS_RESERVATION_OCCUPANTS: readonly EtatReservation[] = ['a_payer', 'confirmee'];

/** Transitions manuelles ; a_payer devient confirmee au paiement (posé par la base, jamais par un agent). */
export const TRANSITIONS_RESERVATION: Record<EtatReservation, ('confirmer' | 'refuser' | 'annuler' | 'terminer')[]> = {
  demandee: ['confirmer', 'refuser', 'annuler'],
  a_payer: ['annuler'],
  confirmee: ['annuler', 'terminer'],
  refusee: [],
  annulee: [],
  terminee: [],
};

const rubrique = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code de rubrique en minuscules');

// Équipements et créneaux ------------------------------------------------------------------------

export const schemaCreationEquipement = z.strictObject({
  bien_id: uuid,
  /** Code fourni (données fictives, tests) ou généré à partir du nom (D-038). */
  code: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,29}$/).optional(),
  nom: z.string().trim().min(2).max(200).optional(),
  description: z.string().trim().max(2000).optional(),
  capacite: z.number().int().min(1).max(100_000).optional(),
  /** Rubrique du catalogue des recettes : l'équipement est payant ; absente : gratuit. */
  rubrique_code: rubrique.optional(),
  ouvert_portail: z.boolean().default(true),
});

export const schemaModificationEquipement = z.strictObject({
  nom: z.string().trim().min(2).max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  capacite: z.number().int().min(1).max(100_000).nullable().optional(),
  rubrique_code: rubrique.nullable().optional(),
  ouvert_portail: z.boolean().optional(),
  actif: z.boolean().optional(),
});

export const schemaCreneauEquipement = z
  .strictObject({ jour_semaine: z.number().int().min(1).max(7), heure_debut: schemaHeure, heure_fin: schemaHeure })
  .refine((c) => c.heure_fin > c.heure_debut, { message: 'La fin du créneau suit son début', path: ['heure_fin'] });
export type CreneauEquipement = z.infer<typeof schemaCreneauEquipement>;

/** Remplacement de l'ensemble des créneaux d'ouverture d'un équipement (sans chevauchement entre eux). */
export const schemaCreneauxEquipement = z.strictObject({
  creneaux: z
    .array(schemaCreneauEquipement)
    .max(70)
    .refine((cs) => !cs.some((a, i) => cs.some((b, j) => j > i && a.jour_semaine === b.jour_semaine && a.heure_debut < b.heure_fin && b.heure_debut < a.heure_fin)), 'Deux créneaux du même jour se chevauchent'),
});

export const schemaRequeteCalendrier = z.strictObject({ debut: schemaJourCivil.optional() });

// Réservations -------------------------------------------------------------------------------------

const plage = {
  date: schemaJourCivil,
  heure_debut: schemaHeure,
  heure_fin: schemaHeure,
  objet: z.string().trim().min(3).max(300),
  participants: z.number().int().min(1).max(100_000).optional(),
};
const finApresDebut = { message: 'L\'heure de fin suit l\'heure de début', path: ['heure_fin'] };

/** Réservation saisie au guichet par un agent : pour un usager inscrit ou un tiers nommé. */
export const schemaCreationReservation = z
  .strictObject({
    equipement_id: uuid,
    ...plage,
    usager_compte_id: uuid.optional(),
    demandeur_nom: z.string().trim().min(2).max(200).optional(),
    demandeur_telephone: z.string().trim().max(30).optional(),
  })
  .refine((v) => v.heure_fin > v.heure_debut, finApresDebut)
  .refine((v) => Boolean(v.usager_compte_id || v.demandeur_nom), { message: 'Indiquer l\'usager ou le nom du demandeur', path: ['demandeur_nom'] });

/** Demande de réservation depuis le portail (l'usager connecté est le demandeur). */
export const schemaDemandeReservationPortail = z
  .strictObject({ collectivite: z.string().trim().min(2).max(10), equipement_id: uuid, ...plage })
  .refine((v) => v.heure_fin > v.heure_debut, finApresDebut);

export const schemaMotifReservation = z.strictObject({ motif: z.string().trim().min(3).max(1000) });

export const schemaFiltreReservations = z.strictObject({
  equipement_id: uuid.optional(),
  etat: z.enum(CODES_ETATS_RESERVATION).optional(),
  du: schemaJourCivil.optional(),
  au: schemaJourCivil.optional(),
  limite: z.coerce.number().int().min(1).max(500).default(200),
});

export const schemaRequeteEquipementsUsager = z.strictObject({ collectivite: z.string().trim().min(2).max(10), debut: schemaJourCivil.optional() });

// Réponses ----------------------------------------------------------------------------------------

export interface Equipement {
  id: string;
  code: string;
  nom: string;
  description: string | null;
  capacite: number | null;
  rubrique_code: string | null;
  payant: boolean;
  ouvert_portail: boolean;
  actif: boolean;
  bien: { id: string; reference: string; categorie: string; localisation: string | null; etat: string };
  creneaux: (CreneauEquipement & { id?: string })[];
}

export interface Reservation {
  id: string;
  numero: string;
  equipement: { id: string; code: string; nom: string };
  date: string;
  heure_debut: string;
  heure_fin: string;
  objet: string;
  participants: number | null;
  demandeur: { usager_compte_id: string | null; nom: string | null; telephone: string | null };
  canal: 'portail' | 'guichet';
  etat: EtatReservation;
  etat_libelle: string;
  motif: string | null;
  montant_estime: number | null;
  regle: string | null;
  fictif: boolean;
  creance: { id: string; numero: string; montant_du: number; reste: number; etat: string } | null;
  transitions: ('confirmer' | 'refuser' | 'annuler' | 'terminer')[];
  demandee_le: string;
  decidee_le: string | null;
  confirmee_le: string | null;
}

export interface PlageCalendrier {
  heure_debut: string;
  heure_fin: string;
  /** Réservations acceptées ou confirmées qui occupent tout ou partie du créneau. */
  occupe: boolean;
}

export interface JourCalendrier {
  date: string;
  jour_semaine: number;
  creneaux: PlageCalendrier[];
  reservations: Pick<Reservation, 'id' | 'numero' | 'heure_debut' | 'heure_fin' | 'etat' | 'etat_libelle' | 'objet'>[];
}

export interface CalendrierEquipement {
  equipement: Pick<Equipement, 'id' | 'code' | 'nom'>;
  debut: string;
  jours: JourCalendrier[];
}

// Règles pures ------------------------------------------------------------------------------------

/** Deux plages horaires [début, fin[ d'un même jour se chevauchent-elles ? */
export function chevauche(a: { heure_debut: string; heure_fin: string }, b: { heure_debut: string; heure_fin: string }): boolean {
  return a.heure_debut < b.heure_fin && b.heure_debut < a.heure_fin;
}

/** Jour ISO de la semaine (1 = lundi ... 7 = dimanche) d'un jour civil. */
export function jourSemaine(jour: string): number {
  const j = new Date(`${jour}T12:00:00Z`).getUTCDay();
  return j === 0 ? 7 : j;
}

/** Ajoute n jours à un jour civil. */
export function ajouterJoursCivils(jour: string, n: number): string {
  return new Date(Date.parse(`${jour}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}

/** La plage demandée tient-elle dans l'un des créneaux d'ouverture du jour ? */
export function dansUnCreneau(creneaux: readonly CreneauEquipement[], date: string, plage: { heure_debut: string; heure_fin: string }): boolean {
  const j = jourSemaine(date);
  return creneaux.some((c) => c.jour_semaine === j && c.heure_debut <= plage.heure_debut && plage.heure_fin <= c.heure_fin);
}

/** Créneaux d'ouverture d'un jour, marqués occupés si une réservation acceptée ou confirmée les recoupe. */
export function creneauxDuJour(creneaux: readonly CreneauEquipement[], date: string, reservations: readonly { heure_debut: string; heure_fin: string; etat: EtatReservation }[]): PlageCalendrier[] {
  const j = jourSemaine(date);
  const occupants = reservations.filter((r) => ETATS_RESERVATION_OCCUPANTS.includes(r.etat));
  return creneaux
    .filter((c) => c.jour_semaine === j)
    .sort((a, b) => a.heure_debut.localeCompare(b.heure_debut))
    .map((c) => ({ heure_debut: c.heure_debut, heure_fin: c.heure_fin, occupe: occupants.some((r) => chevauche(r, c)) }));
}

/** Durée en heures entières commencées d'une plage horaire (base de tarification, D-163). */
export function heuresFacturees(heureDebut: string, heureFin: string): number {
  const minutes = (h: string) => Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5));
  return Math.max(1, Math.ceil((minutes(heureFin) - minutes(heureDebut)) / 60));
}
